import { useCallback, useMemo, useState } from 'react';
import type { FindingAttribute } from '@/models/template';
import type { FindingStatement } from '@/models/report';
import StatementRow from './StatementRow/StatementRow';
import AddStatementRow from './AddStatementRow/AddStatementRow';
import {
  newStatement,
  reconcileStatements,
  statementValues,
} from './utils/statements';
import styles from './FindingInput.module.css';

interface FindingInputProps {
  label: string;
  value: FindingStatement[];
  onChange: (value: FindingStatement[]) => void;
  attributes: FindingAttribute[];
  disabled?: boolean;
  findingClassLabels?: { findingClass: string; label: string }[];
}

export default function FindingInput({
  label,
  value,
  onChange,
  attributes,
  disabled = false,
  findingClassLabels,
}: FindingInputProps) {
  const [addInputValue, setAddInputValue] = useState('');
  const texts = useMemo(() => statementValues(value), [value]);

  const handleSaveStatement = useCallback(
    (index: number, newText: string) => {
      // A reader who changes the text owns the statement from then on. The
      // backend stores the source as sent and never changes it itself.
      onChange(
        value.map((s, i) =>
          i === index && s.value !== newText
            ? { ...s, value: newText, valueSource: 'MANUAL' }
            : s
        )
      );
    },
    [value, onChange]
  );

  const handleDeleteStatement = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  const handleAddStatement = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      onChange([...value, newStatement(trimmed)]);
      setAddInputValue('');
    },
    [value, onChange]
  );

  const handleSetStatements = useCallback(
    (next: string[]) => onChange(reconcileStatements(value, next)),
    [value, onChange]
  );

  return (
    <div className={styles.container} data-finding-input>
      {label && <span className={styles.label}>{label}</span>}

      {value.map((statement, index) => (
        <StatementRow
          key={statement.uuid}
          text={statement.value}
          valueSource={statement.valueSource}
          index={index}
          onSave={handleSaveStatement}
          onDelete={handleDeleteStatement}
          disabled={disabled}
        />
      ))}

      {!disabled && (
        <AddStatementRow
          inputValue={addInputValue}
          onInputChange={setAddInputValue}
          onAdd={handleAddStatement}
          attributes={attributes}
          regionLabel={label || undefined}
          findingClassLabels={findingClassLabels}
          selectedStatements={texts}
          onSetStatements={handleSetStatements}
        />
      )}
    </div>
  );
}
